import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Hoe ziet een freelance contract eruit?',
    answer: 'Een freelance contract begint met de identificatie van beide partijen (freelancer en opdrachtgever), gevolgd door een duidelijke omschrijving van de opdracht en deliverables. Daarna volgen artikelen over vergoeding en betalingsvoorwaarden, looptijd, intellectueel eigendom, geheimhouding, aansprakelijkheid en opzegging. Het contract eindigt met handtekeningen van beide partijen.',
  },
  {
    question: 'Kan ik een gratis freelance contract template gebruiken?',
    answer: 'Ja, met Pactorae kun je gratis een professioneel freelance contract genereren dat volledig is afgestemd op jouw situatie. In tegenstelling tot generieke templates past onze generator het contract aan op basis van jouw specifieke opdracht, tarief en werkwijze. Het resultaat is een juridisch correct contract dat direct bruikbaar is.',
  },
  {
    question: "Wat zijn de risico\'s zonder freelance contract?",
    answer: "Zonder freelance contract loop je als zelfstandige grote risico's: onbetaalde facturen zonder juridische basis om te innen, scope creep waarbij de opdrachtgever steeds meer vraagt zonder extra betaling, geschillen over intellectueel eigendom, geen bescherming bij vroegtijdige beëindiging van de opdracht, en moeilijkheden om afspraken te bewijzen bij een rechtszaak.",
  },
  {
    question: 'Wat moet er minimaal in een freelance contract staan?',
    answer: 'Een freelance contract moet minimaal bevatten: de identiteit van beide partijen, een duidelijke omschrijving van de opdracht en deliverables, het afgesproken tarief en betalingsvoorwaarden, de looptijd van de opdracht, afspraken over intellectueel eigendom, en een clausule over aansprakelijkheid. Dit zijn de absolute basisvereisten voor een geldig en beschermend contract.',
  },
  {
    question: 'Is een mondeling freelance contract geldig?',
    answer: 'Een mondeling freelance contract is in principe juridisch geldig, maar het is extreem moeilijk te bewijzen bij een geschil. Zonder schriftelijk bewijs sta je als freelancer zwak als een opdrachtgever de afspraken ontkent. Gebruik altijd een schriftelijk contract — dit beschermt beide partijen en voorkomt misverstanden.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/freelance-contract-voorbeeld',
      url: 'https://pactorae.com/freelance-contract-voorbeeld',
      name: 'Freelance Contract Voorbeeld (Gratis Template) | Pactorae',
      description:
        'Bekijk een gratis freelance contract voorbeeld en genereer jouw eigen professionele freelance overeenkomst in 30 seconden. Inclusief alle standaard clausules.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-[#2F4E6F] pr-4">{faq?.question}</span>
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
          style={{ backgroundColor: '#FF8C00', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
        </div>
      )}
    </div>
  );
};

const VoorbeeldPreview = () => (
  <div className="relative">
    <div
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      style={{ maxWidth: '720px', margin: '0 auto' }}
    >
      <div style={{ padding: '40px 48px 0 48px', fontFamily: 'Georgia, serif', color: '#1a1a1a', lineHeight: '1.8', fontSize: '14px' }}>
        <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.08em', marginBottom: '4px', textTransform: 'uppercase' }}>FREELANCE OVEREENKOMST</p>
        <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', marginBottom: '32px' }}>Opgesteld via Pactorae — pactorae.com</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>PARTIJEN</p>
        <p style={{ marginBottom: '4px' }}><strong>Opdrachtnemer:</strong> Jan De Vries, Antwerpsestraat 12, 2000 Antwerpen, BTW BE0123.456.789</p>
        <p style={{ marginBottom: '24px' }}><strong>Opdrachtgever:</strong> TechBedrijf BV, Brusselseweg 45, 3000 Leuven, BTW BE0987.654.321</p>

        <p style={{ marginBottom: '16px' }}>Hierna gezamenlijk aangeduid als "de Partijen", komen het volgende overeen:</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 1 — OPDRACHT EN DELIVERABLES</p>
        <p style={{ marginBottom: '8px' }}>Opdrachtnemer verbindt zich ertoe de volgende werkzaamheden uit te voeren:</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Ontwikkeling van een e-commerce website inclusief productcatalogus en winkelwagen</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Integratie van betalingssysteem (Mollie/Stripe)</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Implementatie van CMS voor productbeheer</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Technische documentatie en broncode-overdracht</p>
        <p style={{ marginBottom: '24px', paddingLeft: '16px' }}>• Twee weken technische ondersteuning na oplevering</p>

        <p style={{ marginBottom: '16px' }}>Oplevering: volledig functionele website op <strong>1 juni 2026</strong>.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 2 — VERGOEDING EN BETALINGSVOORWAARDEN</p>
        <p style={{ marginBottom: '8px' }}>Opdrachtgever betaalt Opdrachtnemer een vergoeding van <strong>€ 85,00 per uur</strong>, exclusief BTW (21%). De totale geraamde projectkost bedraagt <strong>€ 5.000,00</strong> op basis van een geschatte 60 uur.</p>
        <p style={{ marginBottom: '8px' }}>Facturatie geschiedt maandelijks op basis van werkelijk gepresteerde uren. Betaaltermijn: <strong>30 dagen</strong> na factuurdatum.</p>
        <p style={{ marginBottom: '24px' }}>Bij aanvang van de opdracht wordt een voorschot van <strong>€ 1.500,00</strong> gefactureerd.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 3 — LOOPTIJD EN DEADLINES</p>
        <p style={{ marginBottom: '16px' }}>Deze overeenkomst treedt in werking op <strong>1 maart 2026</strong> en eindigt na oplevering van alle deliverables, uiterlijk op <strong>1 juni 2026</strong>.</p>
        <p style={{ marginBottom: '24px' }}>Tussentijdse opzegging is mogelijk met inachtneming van een opzegtermijn van <strong>14 kalenderdagen</strong>. Bij opzegging door Opdrachtgever zijn alle reeds verrichte werkzaamheden verschuldigd.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 4 — INTELLECTUEEL EIGENDOM</p>
        <p style={{ marginBottom: '16px' }}>Na volledige betaling van de overeengekomen vergoeding draagt Opdrachtnemer alle intellectuele eigendomsrechten op de ontwikkelde website, broncode en ontwerpen over aan Opdrachtgever. Opdrachtnemer behoudt het recht om het project te vermelden in zijn portfolio.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 5 — GEHEIMHOUDING</p>
        <p style={{ marginBottom: '16px' }}>Opdrachtnemer verbindt zich ertoe alle vertrouwelijke informatie van Opdrachtgever strikt geheim te houden, zowel tijdens als gedurende <strong>2 jaar</strong> na beëindiging van deze overeenkomst. Onder vertrouwelijke informatie wordt verstaan: klantgegevens, bedrijfsstrategieën, financiële informatie en alle overige informatie die als vertrouwelijk is aangemerkt.</p>
      </div>

      {/* Fade-out gradient overlay */}
      <div
        style={{
          height: '160px',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,1) 100%)',
          marginTop: '-80px',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Below fade message */}
      <div style={{ padding: '0 48px 32px 48px', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', marginBottom: '20px' }}>
          Volledig contract genereren met de Pactorae generator
        </p>
        <Link
          to="/contract-generator"
          className="inline-block font-semibold px-8 py-3 rounded-lg text-white transition-colors duration-200 shadow-md hover:shadow-lg"
          style={{ backgroundColor: '#FF8C00' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e07b00'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FF8C00'}
        >
          Genereer jouw contract gratis
        </Link>
      </div>
    </div>
  </div>
);

const FreelanceContractVoorbeeldPage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>Freelance Contract Voorbeeld België &amp; Nederland | Pactorae</title>
        <meta
          name="description"
          content="Bekijk een gratis freelance contract voorbeeld voor België en Nederland. Genereer jouw eigen professionele freelance overeenkomst voor zelfstandigen en zzp'ers in 30 seconden. Inclusief alle standaard clausules."
        />
        <meta
          name="keywords"
          content="freelance contract voorbeeld België, freelance contract voorbeeld Nederland, zzp contract voorbeeld, freelance overeenkomst voorbeeld, gratis freelance contract, zelfstandige contract voorbeeld"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://pactorae.com/freelance-contract-voorbeeld" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/freelance-contract-voorbeeld" />
        <meta property="og:title" content="Freelance Contract Voorbeeld België &amp; Nederland | Pactorae" />
        <meta
          property="og:description"
          content="Bekijk een gratis freelance contract voorbeeld voor België en Nederland. Genereer jouw eigen professionele overeenkomst in 30 seconden."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta property="og:locale" content="nl_NL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Freelance Contract Voorbeeld België &amp; Nederland | Pactorae" />
        <meta
          name="twitter:description"
          content="Bekijk een gratis freelance contract voorbeeld voor België en Nederland. Genereer jouw eigen professionele overeenkomst in 30 seconden."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#FF8C00]/20 text-[#FF8C00] text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-[#FF8C00]/30">
              Gratis Voorbeeld
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Freelance Contract Voorbeeld
              <span className="block text-[#FF8C00] mt-2">België &amp; Nederland</span>
            </h1>
            <p className="text-xl text-blue-100 mb-4 max-w-2xl mx-auto">
              Bekijk een volledig freelance contract voorbeeld en genereer jouw eigen professionele overeenkomst in 30 seconden. Inclusief alle standaard clausules voor zelfstandigen in België en zzp'ers in Nederland.
            </p>
            <p className="text-sm text-blue-200 mb-8 max-w-xl mx-auto">
              Geschikt voor zelfstandigen in België, zzp'ers in Nederland en alle freelance professionals.
            </p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Hoe ziet een freelance contract eruit in België en Nederland?</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Een professioneel freelance contract bestaat uit meerdere artikelen die samen alle aspecten van de samenwerking vastleggen. Of je nu als zelfstandige in België of als zzp'er in Nederland werkt — van de identiteit van beide partijen tot de betalingsvoorwaarden, intellectueel eigendom en aansprakelijkheid. Hieronder vind je een voorbeeld van hoe een freelance contract eruitziet, gevolgd door een overzicht van alle onderdelen.
            </p>
          </section>

          {/* What's included */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Wat bevat een volledig freelance contract?</h2>
            <ul className="space-y-3">
              {[
                { title: 'Partijen', desc: 'Volledige identificatie van freelancer en opdrachtgever' },
                { title: 'Opdracht en deliverables', desc: 'Exacte omschrijving van de werkzaamheden en op te leveren producten' },
                { title: 'Vergoeding', desc: 'Uurtarief of projectprijs, inclusief BTW-afspraken' },
                { title: 'Betalingsvoorwaarden', desc: 'Facturatieritme, betalingstermijn en incassobeleid' },
                { title: 'Looptijd en deadlines', desc: 'Start- en einddatum, tussentijdse mijlpalen' },
                { title: 'Intellectueel eigendom', desc: 'Overdracht van rechten op het geleverde werk' },
                { title: 'Geheimhouding', desc: 'NDA-clausule voor vertrouwelijke informatie' },
                { title: 'Aansprakelijkheid en opzegging', desc: 'Beperking van aansprakelijkheid en opzeggingsvoorwaarden' },
              ]?.map((item, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <span className="font-semibold text-[#2F4E6F]">{item?.title}:</span>{' '}
                    <span className="text-gray-600">{item?.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Voorbeeld Preview */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Freelance contract voorbeeld (volledig ingevuld)</h2>
            <VoorbeeldPreview />
          </section>

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-xl font-bold text-[#2F4E6F] mb-4">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { to: '/freelance-contract-template', label: 'Freelance Contract Template' },
                { to: '/nda-template', label: 'NDA Template voor Freelancers' },
                { to: '/consultancy-contract-template', label: 'Consultancy Contract Template' },
                { to: '/algemene-voorwaarden-generator', label: 'Algemene Voorwaarden Generator' },
              ]?.map((link) => (
                <Link
                  key={link?.to}
                  to={link?.to}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors text-[#2F4E6F] font-medium text-sm"
                >
                  <span style={{ color: '#FF8C00' }}>→</span> {link?.label}
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-8">Veelgestelde vragen over freelance contract voorbeelden</h2>
            <div className="space-y-3">
              {faqs?.map((faq, i) => (
                <FaqItem key={i} faq={faq} />
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl p-12">
            <h2 className="text-2xl font-bold text-white mb-4">Klaar om jouw eigen contract te genereren?</h2>
            <p className="text-blue-100 mb-8">Maak in 30 seconden een professioneel freelance contract op maat — gratis en direct bruikbaar.</p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </section>
        </article>
      </main>
    </>
  );
};

export default FreelanceContractVoorbeeldPage;
